import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Layouts from "../../config/layouts/Layouts";
import NavbarComponent from "../../components/navbar/Navbar";
import Footer from "../../components/footer/Footer";
import {
    postListExcerpt,
    readingTimeMinutesFromHtml,
} from "../../utils/blogExcerpt";
import { isTrustedEmbedUrl, sanitizeBlogHtml } from "../../utils/sanitize";
import { getDateLocale } from "../../utils/locale";
import "../../components/blog/blog.css";

import { BLOG_API as API } from "../../config/api";
import { applySeo } from "../../components/common/Seo";

function BlogPost() {
    const { t } = useTranslation();
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [relatedPosts, setRelatedPosts] = useState([]);

    useEffect(() => {
        const fetchPost = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${API}/posts/${slug}/`);
                setPost(response.data);
                setError(null);
            } catch (err) {
                console.error("Error fetching post:", err);
                setError(t("blogPost.loadError"));
            } finally {
                setLoading(false);
            }
        };

        fetchPost();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    useEffect(() => {
        if (!slug) return;
        const fetchRelated = async () => {
            try {
                const response = await axios.get(
                    `${API}/posts/`,
                    { params: { page: 1 } },
                );
                const list = response.data.results || response.data || [];
                const others = list
                    .filter((p) => p.slug !== slug)
                    .slice(0, 3);
                setRelatedPosts(others);
            } catch (e) {
                setRelatedPosts([]);
            }
        };
        fetchRelated();
    }, [slug]);

    useEffect(() => {
        if (!post) return;
        applySeo({
            title: `${post.title} | EMMPIEZA`,
            description: postListExcerpt(post),
            path: `/blog/${post.slug}`,
            image: post.featured_image_url || "https://emmpieza.com/logo512.png",
            article: {
                headline: post.title,
                datePublished: post.published_at || post.created_at,
                dateModified: post.reviewed_at || post.updated_at,
                author: post.author_name,
                authorRole: post.author_role,
                authorProfile: post.author_profile_url,
                citation: (post.sources || []).map((source) => source.url),
            },
        });
    }, [post]);

    if (loading) {
        return (
            <Layouts>
                <header className="shrink-0"><NavbarComponent /></header>
                <main className="post-detail-page flex items-center justify-center min-h-[60vh]">
                    <div className="spinner"></div>
                </main>
                <Footer />
            </Layouts>
        );
    }

    if (error || !post) {
        return (
            <Layouts>
                <header className="shrink-0"><NavbarComponent /></header>
                <main className="post-detail-page text-center min-h-[60vh] flex flex-col justify-center items-center">
                    <h2 className="text-2xl font-bold text-em-text mb-4">{t("blogPost.error")}</h2>
                    <p className="text-em-soft mb-8 max-w-md">{error}</p>
                    <Link to="/blog" className="text-em-accent hover:text-em-accent2">
                        &larr; {t("blogPost.backToBlog")}
                    </Link>
                </main>
                <Footer />
            </Layouts>
        );
    }

    const date = new Date(post.published_at || post.created_at).toLocaleDateString(getDateLocale(), {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
    const reviewedDate = post.reviewed_at
        ? new Date(post.reviewed_at).toLocaleDateString(getDateLocale(), {
            year: "numeric", month: "long", day: "numeric",
        })
        : null;

    const readMinutes = readingTimeMinutesFromHtml(post.content || "");
    const leadText = postListExcerpt(post);
    const safeContent = sanitizeBlogHtml(post.content || "");
    const safeEmbedUrl = isTrustedEmbedUrl(post.embed_url) ? post.embed_url : null;

    return (
        <Layouts>
            <header className="shrink-0">
                <NavbarComponent />
            </header>

            <main className="post-detail-page">
                <article className="post-article">
                    <Link to="/blog" className="inline-flex items-center gap-2 text-em-accent hover:text-em-accent2 font-semibold mb-8 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                            <path fillRule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" clipRule="evenodd" />
                        </svg>
                        {t("blogPost.backToBlog")}
                    </Link>

                    <header className="post-header">
                        <div className="post-meta">
                            <div className="post-author">
                                <div className="post-author-avatar">
                                    {(post.author_name || "E")[0].toUpperCase()}
                                </div>
                                {post.author_profile_url ? (
                                    <a href={post.author_profile_url} target="_blank" rel="author noopener noreferrer">{post.author_name}</a>
                                ) : post.author_name}
                            </div>
                            <span>•</span>
                            <time dateTime={post.published_at || post.created_at}>{date}</time>
                            {readMinutes != null ? (
                                <>
                                    <span aria-hidden>•</span>
                                    <span className="post-reading-time">
                                        {t("blogPost.minRead", { n: readMinutes })}
                                    </span>
                                </>
                            ) : null}
                        </div>

                        {post.author_role || reviewedDate ? (
                            <div className="post-editorial-meta">
                                {post.author_role ? <span>{post.author_role}</span> : null}
                                {reviewedDate ? <span>{t("blogPost.reviewedOn", { date: reviewedDate })}</span> : null}
                            </div>
                        ) : null}

                        <h1 className="post-title">{post.title}</h1>

                        {leadText ? (
                            <p className="post-lead">
                                {leadText}
                            </p>
                        ) : null}
                    </header>

                    {post.featured_image_url && (
                        <img
                            src={post.featured_image_url}
                            alt={post.title}
                            className="post-hero-img"
                        />
                    )}

                    {safeEmbedUrl && (
                        <div className="post-embed-container">
                            <iframe
                                src={safeEmbedUrl}
                                title={t("blogPost.embedTitle")}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                referrerPolicy="strict-origin-when-cross-origin"
                            ></iframe>
                        </div>
                    )}

                    <div
                        className="post-content"
                        dangerouslySetInnerHTML={{ __html: safeContent }}
                    />

                    {post.methodology ? (
                        <section className="post-evidence" aria-labelledby="post-methodology-title">
                            <h2 id="post-methodology-title">{t("blogPost.methodologyTitle")}</h2>
                            <p>{post.methodology}</p>
                        </section>
                    ) : null}

                    {post.sources?.length ? (
                        <section className="post-evidence" aria-labelledby="post-sources-title">
                            <h2 id="post-sources-title">{t("blogPost.sourcesTitle")}</h2>
                            <ol className="post-sources-list">
                                {post.sources.map((source) => (
                                    <li key={source.id}>
                                        <a href={source.url} target="_blank" rel="noopener noreferrer">{source.title}</a>
                                        {source.publisher ? <span> — {source.publisher}</span> : null}
                                        {source.is_primary ? <strong>{t("blogPost.primarySource")}</strong> : null}
                                    </li>
                                ))}
                            </ol>
                        </section>
                    ) : null}

                    {post.attachments && post.attachments.length > 0 && (
                        <div className="post-attachments">
                            <h3>{t("blogPost.attachmentsTitle")}</h3>
                            <div className="attachment-list">
                                {post.attachments.map((file) => (
                                    <a key={file.id} href={file.file_url} target="_blank" rel="noopener noreferrer" className="attachment-item">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="attachment-icon">
                                            <path fillRule="evenodd" d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0016.5 9h-1.875a1.875 1.875 0 01-1.875-1.875V5.25A3.75 3.75 0 009 1.5H5.625zM7.5 15a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5A.75.75 0 017.5 15zm.75 2.25a.75.75 0 000 1.5H12a.75.75 0 000-1.5H8.25z" clipRule="evenodd" />
                                            <path d="M12.971 1.816A5.23 5.23 0 0114.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 013.434 1.279 9.768 9.768 0 00-6.963-6.963z" />
                                        </svg>
                                        {file.title || t("blogPost.downloadAttachment")}
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    {relatedPosts.length > 0 ? (
                        <section className="post-related" aria-labelledby="post-related-heading">
                            <h2 id="post-related-heading" className="post-related-title">
                                {t("blogPost.moreArticles")}
                            </h2>
                            <ol className="post-related-list">
                                {relatedPosts.map((rp) => (
                                    <li key={rp.id} className="post-related-item">
                                        <Link to={`/blog/${rp.slug}`} className="post-related-link">
                                            <span className="post-related-item-title">{rp.title}</span>
                                            <span className="post-related-item-arrow" aria-hidden>→</span>
                                        </Link>
                                    </li>
                                ))}
                            </ol>
                            <Link to="/blog" className="post-related-all">
                                {t("blogPost.viewAllArticles")}
                            </Link>
                        </section>
                    ) : null}
                </article>
            </main>

            <Footer />
        </Layouts>
    );
}

export default BlogPost;
